import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { REALTOR_SERVICES_V2 } from '../../constants/agreements/realtorServicesV2';
import { WHOLESALE_BROKER_V2 } from '../../constants/agreements/wholesaleBrokerV2';
import { BROKER_COMP_PCT_LABEL, REALTOR_COMP_PCT_LABEL } from '../../constants/commissionRates';
import { RequirePartnerAuth } from '../../components/DoorlyProLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const highlight = {
  background: '#eff6ff',
  padding: '1px 4px',
  borderRadius: 3,
};

function H({ children }) {
  return <span style={highlight}>{children}</span>;
}


// Shown when no agreement corresponds to this partner's type — an unset type, or
// a homebuilder (no homebuilder agreement exists). Deliberately offers nothing to
// sign: serving the wrong agreement is a money obligation, and a blank state is
// recoverable where a wrong signature is not.
function NoAgreementForType() {
  return (
    <div style={{ padding: 24, textAlign: 'center' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>No agreement to sign yet</h2>
      <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
        We&rsquo;re confirming your partner type. A Doorly team member will be in touch about your
        agreement — there&rsquo;s nothing for you to do right now.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ THE REALTOR AGREEMENT IS NO LONGER WRITTEN HERE. It renders from
// constants/agreements/realtorServicesV2.js — the same structured document the
// backend renders into the signed PDF — so what a partner READS and what they
// SIGN cannot diverge. They did diverge before: the loan-officer rate said 0.25
// percent in the PDF and 1.00 percent on this page for three and a half months,
// because each side carried its own prose and nothing compared them.
//
// backend/test/agreementTextParity.test.js now asserts the two copies are
// clause-for-clause identical.
//
// ⚠️ THE R-WORD STILL MUST NOT NAME THE INSTRUMENT. RESPA §8 — see
// constants/partnerTypes.js and the guard in test/referralNaming.test.js.
function RealtorAgreement({ profile, today }) {
  const ls = profile.license_state || 'not on file';
  const brokerName = profile.name
    || `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
    || '[Broker]';
  const brokerage = profile.brokerage || '[Brokerage]';
  // Tokens are shared with the PDF renderer so neither side invents its own
  // wording around a blank.
  // ── 🚨 THE RESALE CAP COMES FROM THE PROGRAM, NOT FROM THIS FILE ─────────
  // Clause 3.2 used to state "(not to exceed 5%)" as literal text while the
  // program cap is 10%. Retyping 10% here would only move the drift; rendering
  // it from RESALE_ADJUSTMENT_OPTIONS means the agreement follows the program by
  // construction, and the backend PDF renderer fills the same token the same way.
  const fill = (t) => String(t)
    .split('{{DATE}}').join(today)
    .split('{{BROKER_NAME}}').join(brokerName)
    .split('{{LICENSE_STATE}}').join(ls)
    .split('{{BROKERAGE}}').join(brokerage)
    // §4.1's rate, filled from the mirrored constant — see commissionRates.js.
    .split('{{REALTOR_COMP_PCT}}').join(REALTOR_COMP_PCT_LABEL);

  return (
    <>
      <h2 style={{ fontSize: 20, fontWeight: 700, textAlign: 'center', marginBottom: 4 }}>REALTOR SERVICES AGREEMENT</h2>
      {/* The version is on the document a partner reads, as it is on the one they
          sign — a version recorded only in a database column cannot be cited from
          either. */}
      <p style={{ textAlign: 'center', fontSize: 12, color: '#6b7280', marginBottom: 24 }}>Version 2.2</p>

      {REALTOR_SERVICES_V2.map((e, i) => {
        if (e.kind === 'section') {
          return <h3 key={i} style={{ fontSize: 16, fontWeight: 700, marginTop: 28 }}>{e.n}. {e.t.toUpperCase()}</h3>;
        }
        if (e.kind === 'p') return <p key={i}>{fill(e.t)}</p>;
        return (
          <div key={i}>
            <p><strong>{e.n}</strong>{e.t ? ` ${fill(e.t)}` : ''}</p>
            {(e.body || []).map((b, j) => <p key={j} style={{ marginLeft: 16 }}>{fill(b)}</p>)}
          </div>
        );
      })}

      <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 28 }}>SIGNATURE</h3>
      <p>Go Doorly, LLC d/b/a Doorly</p>
      <p>Broker: <H>{brokerName}</H></p>
      <p>Brokerage: <H>{brokerage}</H></p>
      <p>License Number: <H>{profile.license_number || 'not on file'}</H></p>
      <p>Email: <H>{profile.email || 'not on file'}</H></p>
      <p>Date: <H>{today}</H></p>
    </>
  );
}

// 🚨 RENDERS THE WHOLESALE BROKER AGREEMENT v2.1 FROM THE SHARED CONSTANT. What
// was here was the v1.0 REFERRAL agreement, hand-written a second time — its
// §2.2(c) forbade "any mortgage loan origination activities, including taking
// loan applications", which is the entire thing the wholesale channel does.
//
// ⚠️ TWO HAND-WRITTEN COPIES IS THE DEFECT THIS PATTERN EXISTS FOR. The rate
// read 0.25% in the signed PDF and 1.00% on this page for three and a half
// months, because nothing could compare prose to prose. Both renderers now read
// constants/agreements/wholesaleBrokerV2.js and the parity test compares them
// clause by clause.
//
// ⚠️ ONE MORE TOKEN THAN THE REALTOR INSTRUMENT: {{ENTITY_TYPE}}. This agreement
// is executed by a company, and the preamble has an entity-form blank the
// realtor document has no equivalent of. There is no profile field for it yet,
// so it shows as a visible placeholder rather than collapsing to nothing.
function LoanOfficerAgreement({ profile, today }) {
  const ls = profile.license_state || 'not on file';
  const brokerage = profile.brokerage || '[Brokerage]';
  const brokerName = profile.brokerage || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || '[Broker]';
  const fill = (t) => String(t)
    .split('{{DATE}}').join(today)
    .split('{{BROKER_NAME}}').join(brokerName)
    .split('{{ENTITY_TYPE}}').join(profile.entity_type || '[entity type]')
    // 🔑 THE RATE, RENDERED — NOT RETYPED. §4 said "one percent (1.00%)" as
    // literal text. See frontend/src/constants/commissionRates.js.
    .split('{{BROKER_COMP_PCT}}').join(BROKER_COMP_PCT_LABEL)
    .split('{{LICENSE_STATE}}').join(ls)
    .split('{{BROKERAGE}}').join(brokerage);

  return (
    <>
      <h2 style={{ fontSize: 20, fontWeight: 700, textAlign: 'center', marginBottom: 4 }}>WHOLESALE BROKER AGREEMENT</h2>
      <p style={{ textAlign: 'center', fontSize: 12, color: '#6b7280', marginBottom: 24 }}>Version 2.1</p>

      {WHOLESALE_BROKER_V2.map((e, i) => {
        if (e.kind === 'section') {
          return <h3 key={i} style={{ fontSize: 16, fontWeight: 700, marginTop: 28 }}>{e.n}. {e.t.toUpperCase()}</h3>;
        }
        if (e.kind === 'p') return <p key={i}>{fill(e.t)}</p>;
        return (
          <div key={i}>
            <p><strong>{e.n}</strong>{e.t ? ` ${fill(e.t)}` : ''}</p>
            {(e.body || []).map((b, j) => <p key={j} style={{ marginLeft: 16 }}>{fill(b)}</p>)}
          </div>
        );
      })}

      {/* ── 🚨 THE BODY'S OWN SIGNATURE BLOCK IS GONE, AND THAT IS THE FIX ──
          It rendered as bare paragraphs — "Company / Authorized Signer /
          Company NMLS #" with the reader's own name and email in them —
          directly above the two carded blocks that are the live signature
          surface. So the page showed the signature twice, once uncarded, and
          the uncarded one named the reader as the FIRM'S authorized signer.
          An individual loan officer read it as signing for their company.

          🔑 AND IT CORRESPONDED TO NOTHING IN THE EXECUTED DOCUMENT.
          buildAgreementPdf prints "SIGNED BY <partner>" and then
          "COUNTER-SIGNED FOR <FIRM>" — the member signs as themselves and the
          firm's broker of record counter-signs for the entity. There is no
          "Authorized Signer" line anywhere in the PDF. Removing this makes the
          screen match the instrument.

          ⚠️ SO IT IS NOT A TERMS CHANGE AND DOES NOT BUMP THE VERSION. See
          constants/partnerAgreementVersion.js: bump when a partner's
          obligations or compensation would differ. Nothing in the PDF moved. */}
    </>
  );
}

function SignatureBlockDoorly({ today }) {
  return (
    <div style={{ marginTop: 40, borderTop: '2px solid #e5e7eb', paddingTop: 28 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>GO DOORLY, LLC d/b/a DOORLY:</h3>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        {[
          { label: 'By:', value: 'Jesse Coody, CEO' },
          { label: 'Name:', value: 'Jesse Coody' },
          { label: 'Title:', value: 'Chief Executive Officer' },
          { label: 'Date:', value: today },
        ].map((row) => (
          <div key={row.label} style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '9px 16px' }}>
            <span style={{ width: 80, fontSize: 13, fontWeight: 600, color: '#9ca3af', flexShrink: 0 }}>{row.label}</span>
            <span style={{ fontSize: 13, color: '#111827' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignatureBlockRealtor({ profile, today }) {
  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>BROKER / AGENT:</h3>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        {[
          { label: 'Name:', value: <H>{profile.first_name} {profile.last_name}</H> },
          { label: 'Brokerage:', value: <H>{profile.brokerage || '—'}</H> },
          { label: 'License #:', value: <H>{profile.license_number || '—'}</H> },
          { label: 'Date:', value: today },
          { label: 'Email:', value: <H>{profile.email || '—'}</H> },
        ].map((row, i) => (
          <div key={i} style={{ display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '9px 16px' }}>
            <span style={{ width: 100, fontSize: 13, fontWeight: 600, color: '#9ca3af', flexShrink: 0 }}>{row.label}</span>
            <span style={{ fontSize: 13, color: '#111827' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WHO IS SIGNING, AND IN WHAT CAPACITY.
//
// 🚨 THIS BLOCK WAS ALWAYS THE FIRM'S. One component, headed "BROKER/IMB:",
// naming whoever opened the page as the company's "Authorized Signer", with an
// editable Title and a "Company NMLS #" that rendered their INDIVIDUAL number —
// and the same number again on the next row as "Individual NMLS #". An
// individual loan officer who is not the broker of record was shown a block
// saying they were binding their company.
//
// 🔑 THE EXECUTED DOCUMENT NEVER SAID THAT. buildAgreementPdf prints "SIGNED BY
// <partner>" and then "COUNTER-SIGNED FOR <FIRM>" — the member signs as
// themselves, the firm's broker of record counter-signs for the entity. So the
// screen was misdescribing the instrument, and this makes it match.
//
// ⚠️ THE TITLE FIELD WAS REQUIRED AND WENT NOWHERE. `signer_title` is posted and
// no server route reads it — not stored, not printed on the PDF. It is asked for
// only where it means something (somebody signing in the firm's name) and is
// reported as unused either way.
// ─────────────────────────────────────────────────────────────────────────────
const SIG_ROW = { display: 'flex', borderBottom: '1px solid #f3f4f6', padding: '9px 16px' };
const SIG_LABEL = { width: 140, fontSize: 13, fontWeight: 600, color: '#9ca3af', flexShrink: 0 };
const SIG_VALUE = { fontSize: 13, color: '#111827' };

function SignatureBlockLoanOfficer({ profile, today, signerTitle, setSignerTitle }) {
  // 🚨 THE CAPACITY COMES FROM account_role, THE SAME FIELD THE COUNTER-SIGNATURE
  // QUEUE AND THE PAYOUT GATE READ. Not from the partner type: every loan
  // officer is a loan officer, and only one of them per firm signs for it.
  const signsForFirm = profile.account_role === 'broker';
  const firmName = profile.account_company_name || profile.brokerage || '—';

  const rows = signsForFirm
    ? [
      ['Company:', <H>{firmName}</H>],
      ['Authorized Signer:', <H>{`${profile.first_name || ''} ${profile.last_name || ''}`.trim()}</H>],
      ['Title:', 'TITLE_INPUT'],
      // ⚠️ THE FIRM'S OWN REGISTRATION, from partner_accounts.company_nmls. This
      // row printed partner_profiles.nmls_id — the individual number — under a
      // "Company NMLS #" label, on the instrument Doorly executes WITH the
      // entity.
      ['Company NMLS #:', <H>{profile.account_company_nmls || '[not on record]'}</H>],
      ['Individual NMLS #:', <H>{profile.nmls_id || '[not on record]'}</H>],
      ['Date:', today],
      ['Email:', <H>{profile.email || '—'}</H>],
    ]
    : [
      // 🔑 A MEMBER SIGNS AS THEMSELVES. No company, no Authorized Signer, no
      // Title — they are not signing in an office of the company, and offering
      // the field invited them to claim one.
      ['Name:', <H>{`${profile.first_name || ''} ${profile.last_name || ''}`.trim()}</H>],
      ['Individual NMLS #:', <H>{profile.nmls_id || '[not on record]'}</H>],
      ['Date:', today],
      ['Email:', <H>{profile.email || '—'}</H>],
    ];

  return (
    <div style={{ marginTop: 32 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 14,
        textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {signsForFirm ? `BROKER/IMB — ${firmName}:` : 'LOAN OFFICER (YOU):'}
      </h3>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        {rows.map(([label, value], i) => (
          <div key={label} style={i === rows.length - 1
            ? { ...SIG_ROW, borderBottom: 'none' } : SIG_ROW}>
            <span style={SIG_LABEL}>{label}</span>
            {value === 'TITLE_INPUT' ? (
              <input
                type="text"
                value={signerTitle}
                onChange={(e) => setSignerTitle(e.target.value)}
                placeholder="e.g. President, Owner"
                required
                style={{ flex: 1, height: 32, padding: '0 10px', borderRadius: 6,
                  border: '1px solid #d1d5db', fontSize: 13, outline: 'none', color: '#111827' }}
              />
            ) : <span style={SIG_VALUE}>{value}</span>}
          </div>
        ))}
      </div>
      {/* 🚨 WHO SIGNS THE OTHER HALF, SAID ON THE SCREEN. A member reading one
          signature block reasonably concludes one signature finishes the
          document. It does not: the firm's side is the counter-signature, and
          the PDF prints an empty line for it saying so. */}
      {!signsForFirm && (
        <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 8, lineHeight: 1.6 }}>
          You&rsquo;re signing as yourself. Doorly contracts with{' '}
          <strong>{firmName}</strong>, and your firm&rsquo;s broker of record signs the
          company&rsquo;s side — that counter-signature is a separate step and it is not yours
          to give.
        </p>
      )}
    </div>
  );
}

// 🔑 BEHIND THE SIGN-IN CHECK, LIKE EVERY OTHER SIGNED-IN DoorlyPro PAGE (Hannah, 2026-11-02). RequirePartnerAuth
// holds the render until the session is read and sends a signed-out visitor to /doorlypro/login?next=<this page>.
export default function DoorlyProAgreement() {
  return (
    <RequirePartnerAuth>
      <DoorlyProAgreementPage />
    </RequirePartnerAuth>
  );
}

function DoorlyProAgreementPage() {
  const navigate = useNavigate();
  const { token } = useDoorlyProAuth();

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [hasScrolled, setHasScrolled] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  // Declared beside the state it reads so the button and the field cannot
  // disagree about whether a Title is needed.

  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);
  const scrollContainerRef = useRef(null);

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/doorlypro/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setProfile(res.data.partner || res.data))
      .catch(() => setError('Failed to load profile.'))
      .finally(() => setLoadingProfile(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll tracking ──────────────────────────────────────────────────────
  function handleScroll() {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom) setHasScrolled(true);
  }

  // ── Canvas helpers ────────────────────────────────────────────────────────
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
  };

  const startDraw = (e) => {
    isDrawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
  };

  const draw = (e) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => {
    isDrawing.current = false;
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const data = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height).data;
    setSigned(data.some((v) => v !== 0));
  };

  const clearSignature = () => {
    if (canvasRef.current) {
      canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setSigned(false);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleSign() {
    setSubmitting(true);
    setError('');
    try {
      const sigDataUrl = canvasRef.current.toDataURL('image/png');
      const preFilledFields = {
        name: `${profile.first_name} ${profile.last_name}`,
        email: profile.email,
        brokerage: profile.brokerage,
        license_number: profile.license_number,
        license_state: profile.license_state,
        nmls_id: profile.nmls_id,
        // ⚠️ SENT ONLY BY SOMEBODY SIGNING IN THE FIRM'S NAME. It was sent by
        // every loan officer, from a required field, and NO server route reads
        // it — not stored, not printed on the PDF. Reported as unused rather
        // than quietly kept.
        signer_title: (isLoanOfficer && profile.account_role === 'broker' && signerTitle)
          ? signerTitle : undefined,
      };
      // ⚠️ THE RESPONSE IS NO LONGER READ. It was consulted only to decide which
      // screen to return to, and that decision is gone — the request is still
      // awaited, so a failure still lands in the catch below.
      await axios.post(
        `${API_URL}/api/doorlypro/onboarding/agreement`,
        {
          partner_type: profile.partner_type,
          signed_at: new Date().toISOString(),
          signature_data: sigDataUrl,
          pre_filled_fields: preFilledFields,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // 🚨 THE DASHBOARD, UNLESS THEY CAME FROM SOMEWHERE THAT SENT THEM HERE.
      //
      // This was unconditionally the dashboard, and the reasoning still holds
      // for the ordinary case: it branched on onboarding_status and sent anyone
      // not yet 'active' into /doorlypro/onboarding — a sequential flow that no
      // longer exists. Signing is a dashboard task now, so the way back from it
      // is the place it was started from.
      //
      // 🔑 AND THAT IS EXACTLY WHY A RETURN PATH BELONGS HERE. "The place it was
      // started from" is the dashboard when the task was started there, and the
      // Submit Application page when a broker was stopped mid-intent by the
      // agreement overlay. Sending them to the dashboard after signing makes
      // "sign and continue" two motions and leaves them to find their way back
      // to what they were doing.
      //
      // ⚠️ AN ENUMERATED LIST, NOT A PREFIX MATCH. `return` arrives in a query
      // string a partner can edit, so accepting it verbatim would be an open
      // redirect — //evil.example and javascript: both survive a naive
      // startsWith('/') check, the first because a protocol-relative URL IS a
      // path to the browser.
      //
      // 🚨 AND A /doorlypro/ PREFIX WAS NOT ENOUGH EITHER. The first cut of this
      // allowed any path under /doorlypro/, which permitted
      // /doorlypro/onboarding — the retired sequential stepper that
      // test/brokerDashboard.test.js exists to keep partners out of. It caught
      // it. A prefix is a shape-match over a space nobody enumerated; this is
      // the list of places it is actually correct to send somebody after they
      // sign, and adding one is a decision rather than an accident.
      const RETURN_ALLOWED = ['/doorlypro/submissions'];
      const params = new URLSearchParams(window.location.search);
      const requested = params.get('return') || '';
      const safeReturn = RETURN_ALLOWED.includes(requested) ? requested : null;
      navigate(safeReturn || '/doorlypro/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit agreement.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingProfile || !profile) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6b7280' }}>
          <svg style={{ display: 'block', margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} width="32" height="32" fill="none" viewBox="0 0 24 24">
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="#3a6ad9" strokeWidth="4" />
            <path style={{ opacity: 0.75 }} fill="#3a6ad9" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p style={{ fontSize: 14 }}>{error || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // FAIL CLOSED. This was `isLoanOfficer ? LoanOfficer : Realtor`, so a
  // homebuilder — or a partner with no type at all — was served the Realtor
  // agreement and its 2% referral fee. An unrecognised type now reaches NO
  // document. The server gates the POST too; this is the visible half.
  const AGREEMENT_TYPES = ['realtor', 'loan_officer'];
  const hasAgreement = AGREEMENT_TYPES.includes(profile.partner_type);
  const isLoanOfficer = profile.partner_type === 'loan_officer';
  const titleMissing = isLoanOfficer && profile && profile.account_role === 'broker'
    && !signerTitle.trim();

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '32px 16px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* ── 🚨 A WAY OUT WITHOUT SIGNING ────────────────────────────────
            This page had no exit. A partner who opened the agreement to read it
            and was not ready to sign had to use the browser's back button or
            retype a URL — on the one screen where the only forward action is
            executing a contract. An unsigned agreement is a legitimate place to
            stop, and a reader with no visible way to stop reads the signature
            button as the only option.

            ⚠️ IT SIGNS NOTHING AND SAVES NOTHING. It navigates; the agreement
            is unchanged and the task stays on their dashboard. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, marginBottom: 24 }}>
          <img
            src={`${process.env.PUBLIC_URL}/assets/DoorlyPro.png`}
            alt="DoorlyPro"
            height="32"
            style={{ height: 32, width: 'auto' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <button
            type="button"
            onClick={() => navigate('/doorlypro/dashboard')}
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6,
              background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
              padding: '7px 13px', fontSize: 13, fontWeight: 600, color: '#374151',
              cursor: 'pointer' }}>
            Close without signing
          </button>
        </div>

        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#111827', marginBottom: 6 }}>Partner Agreement</h1>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>
          Please read the full agreement below before signing. You can close this and come back
          to it — nothing is signed until you sign it.
        </p>

        {/* Agreement document container */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: '40px',
            maxHeight: '60vh',
            overflowY: 'scroll',
            fontFamily: 'Georgia, serif',
            lineHeight: 1.8,
            fontSize: 15,
            color: '#111827',
          }}
        >
          {!hasAgreement
            ? <NoAgreementForType />
            : isLoanOfficer
            ? <LoanOfficerAgreement profile={profile} today={today} />
            : <RealtorAgreement profile={profile} today={today} />
          }

          {/* Doorly signature block inside scrollable area */}
          <SignatureBlockDoorly today={today} />

          {/* Partner signature block inside scrollable area */}
          {isLoanOfficer
            ? <SignatureBlockLoanOfficer profile={profile} today={today} signerTitle={signerTitle} setSignerTitle={setSignerTitle} />
            : <SignatureBlockRealtor profile={profile} today={today} />
          }

          <div style={{ height: 24 }} />
        </div>

        {!hasScrolled && (
          <p style={{ color: '#6b7280', textAlign: 'center', marginTop: 12, fontSize: 13 }}>
            Please scroll to the bottom to continue
          </p>
        )}

        {/* Signature section — disabled until scrolled */}
        <div style={{ opacity: hasScrolled ? 1 : 0.4, pointerEvents: hasScrolled ? 'auto' : 'none', marginTop: 28 }}>

          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 20 }}>
            {/* 🚨 THE CARD NAMES THE PARTY. The canvas sat under a bare "Draw
                your signature below", directly beneath the signature blocks —
                so the act of signing was attached to whichever block the reader
                had last looked at, and for a loan officer that block said
                BROKER/IMB. Naming the signer here means the signature cannot be
                read as given in somebody else's capacity. */}
            <p style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 2 }}>
              Draw your signature below:
            </p>
            <p style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 10 }}>
              Signing as <strong>{`${profile.first_name || ''} ${profile.last_name || ''}`.trim()}</strong>
              {isLoanOfficer && profile.account_role !== 'broker'
                ? ' — as yourself, not for your firm.'
                : profile.account_company_name
                  ? ` for ${profile.account_company_name}.`
                  : '.'}
            </p>
            <div style={{ position: 'relative' }}>
              <canvas
                ref={canvasRef}
                width={860}
                height={160}
                style={{
                  width: '100%',
                  height: 160,
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: '#fafafa',
                  cursor: 'crosshair',
                  display: 'block',
                  touchAction: 'none',
                }}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={(e) => { e.preventDefault(); startDraw(e); }}
                onTouchMove={(e) => { e.preventDefault(); draw(e); }}
                onTouchEnd={(e) => { e.preventDefault(); stopDraw(); }}
              />
              {!signed && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <span style={{ fontSize: 13, color: '#d1d5db' }}>Sign here</span>
                </div>
              )}
            </div>
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={clearSignature}
                style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Checkbox */}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#374151', cursor: 'pointer', marginBottom: 20 }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ marginTop: 2, accentColor: '#3a6ad9', width: 16, height: 16, flexShrink: 0 }}
            />
            I confirm I have read and agree to the terms of this Agreement
          </label>

          {error && (
            <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, background: '#fff0f3', border: '1px solid #ffb3bf', color: '#ff082f', fontSize: 13 }}>
              {error}
            </div>
          )}
          {/* Submit button */}
          {/* 🔑 THE TITLE IS A PRECONDITION ONLY WHERE IT IS ASKED FOR. It
              carried `required` on the input, which does nothing outside a form
              — so an empty Title never blocked anything, and a member who was
              shown the field could sign without it anyway. */}
          <button
            disabled={!signed || !agreed || submitting || titleMissing}
            onClick={handleSign}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 10,
              border: 'none',
              backgroundColor: !signed || !agreed || submitting ? '#9ca3af' : '#3a6ad9',
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              cursor: !signed || !agreed || submitting ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {submitting ? 'Signing...' : 'Sign Agreement'}
          </button>
        </div>
      </div>
    </div>
  );
}
